import { toUnit } from '../../lib/cityGeo'

/**
 * The signature element.
 *
 * A tiny chart of the town: every dog park in it as a faint dot, and THIS park lit in the
 * marker colour. Drawn from coordinates we already hold, so it costs nothing, needs no API,
 * and works on all 2,215 parks.
 *
 * It exists because it is the only honest way to tell the parks apart. OpenStreetMap has no
 * name for four in five Spanish dog parks (23 of Sevilla's 25 are unnamed) and no recorded
 * attributes for most of them (20 of 25). A list of identical "Zona canina" rows cannot be
 * fixed with typography. Showing *where each one is* fixes it, because that is the one thing
 * we actually know.
 *
 * Server-rendered inline SVG: no JavaScript, no request, and it is in the HTML for crawlers.
 *
 * IT USED TO BE O(n²). Each glyph re-drew EVERY park in the town, and there is one glyph per row —
 * so Madrid's 153 rows emitted 153 x 153 = 23,567 <circle> elements and the page weighed 3.03 MB of
 * HTML. Each <li> was ~17 KB. Gzip hid it on the wire, but the browser and Googlebot still parsed
 * all of it, and the DOM was 25,830 nodes against Lighthouse's 1,500-node "excessive" threshold.
 *
 * The context dots are IDENTICAL in every glyph in a city — same parks, same bounds. So they are
 * now drawn ONCE into an SVG <defs> (see CityDots below) and each glyph references them with a
 * single <use>. Same picture, O(n) markup: 23,567 circles -> 153 + one shared cloud.
 */

/**
 * The town's dot cloud, emitted ONCE per page. Render this before any ParkGlyph that shares its
 * `id`. It is invisible on its own — it exists only to be <use>d.
 */
export function CityDots({ id, parks, bounds }) {
  return (
    <svg width="0" height="0" aria-hidden="true" focusable="false" style={{ position: 'absolute' }}>
      <defs>
        <g id={id}>
          {parks.map((p) => {
            const u = toUnit(p, bounds)
            return (
              <circle
                key={p.id}
                cx={4 + u.x * 92}
                cy={4 + u.y * 92}
                r="1.6"
                fill="currentColor"
                opacity="0.22"
              />
            )
          })}
        </g>
      </defs>
    </svg>
  )
}
export default function ParkGlyph({ park, parks, bounds, size = 68, dotsId = null }) {
  const here = toUnit(park, bounds)

  return (
    <svg
      className="glyph"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-label={`Situación de este parque dentro del municipio`}
    >
      {/* Faint grid: a chart has one. Four lines, no more. */}
      <g stroke="currentColor" strokeWidth="0.5" opacity="0.10">
        <line x1="25" y1="4" x2="25" y2="96" />
        <line x1="50" y1="4" x2="50" y2="96" />
        <line x1="75" y1="4" x2="75" y2="96" />
        <line x1="4" y1="50" x2="96" y2="50" />
      </g>

      {/* Every other park in the town. Context, quietly — and drawn ONCE for the whole page.
          `dotsId` points at a <CityDots> the parent rendered. Without it we fall back to drawing
          them inline, which is correct but is the O(n²) path: only do that for short lists. */}
      {dotsId ? (
        <use href={`#${dotsId}`} />
      ) : (
        parks.map((p) => {
          if (p.id === park.id) return null
          const u = toUnit(p, bounds)
          return (
            <circle
              key={p.id}
              cx={4 + u.x * 92}
              cy={4 + u.y * 92}
              r="1.6"
              fill="currentColor"
              opacity="0.22"
            />
          )
        })
      )}

      {/* This one. The only saturated mark on the card, and the reason the card works. */}
      <g>
        <circle
          cx={4 + here.x * 92}
          cy={4 + here.y * 92}
          r="6.5"
          fill="var(--c-primary)"
          opacity="0.16"
        />
        <circle
          cx={4 + here.x * 92}
          cy={4 + here.y * 92}
          r="3"
          fill="var(--c-primary)"
        />
      </g>
    </svg>
  )
}
